<template>
  <div class="changed-actions">
    <Message v-if="error" severity="error" :closable="false">
      {{ t("global.error") }}
    </Message>
    <div v-else-if="!loading && events.length === 0" class="text-center py-5 text-color-secondary">
      {{ t("global.noData") }}
    </div>
    <EventList
      v-else
      :events="events"
      :loading="loading"
      :event-types="eventTypes"
      :action-types="actionTypes"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, provide } from "vue";
import Message from "primevue/message";
import EventList from "@/components/event-action/event/EventList.vue";
import type { CombinedLoginPayload } from "@/license/timeStampClock/helper/TCTypes";
import { useTranslation } from "@/language/translation";
import {
  IssueOrder,
  SQDCEventActionTypeType,
  useSQDCEventActionTypesQuery,
  useTCStampChangedActionEventsQuery,
} from "@/graphql";
import { useEventTranslationLayer } from "@/views/event-action/EventTranslationLayer";
import {
  BoardIsLoadingInjectionKey,
  ReloadBoardInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";

const props = defineProps<{
  login: CombinedLoginPayload;
}>();

const { t } = useTranslation();
const variables = computed(() =>
  "code" in props.login
    ? { code: props.login.code }
    : {
        login: {
          username: props.login.username,
          password: props.login.password,
        },
      },
);

const {
  result,
  loading: loadingEvents,
  error,
  refetch,
} = useTCStampChangedActionEventsQuery(variables, {
  fetchPolicy: "network-only",
});

const changedEvents = computed(() => result.value?.TCStampChangedActionEvents ?? []);
const typeIds = computed(() => [
  ...new Set(
    changedEvents.value.flatMap((event) => [
      event.typeId,
      ...event.ActionEventConnection.flatMap(({ action }) =>
        action == null ? [] : [action.typeId],
      ),
    ]),
  ),
]);
const { result: typeResult, loading: loadingTypes } = useSQDCEventActionTypesQuery(
  () => ({ ids: typeIds.value }),
  () => ({
    enabled: typeIds.value.length > 0,
    fetchPolicy: "cache-first",
  }),
);

function toEABType(type: NonNullable<typeof typeResult.value>["SQDCEventActionTypes"][number]) {
  return {
    ...type,
    fields: type.SQDCEventActionTypeFields,
  };
}

const eventTypes = computed(() =>
  (typeResult.value?.SQDCEventActionTypes ?? [])
    .filter((type) => type.type === SQDCEventActionTypeType.EVENT)
    .map(toEABType),
);
const actionTypes = computed(() =>
  (typeResult.value?.SQDCEventActionTypes ?? [])
    .filter((type) => type.type === SQDCEventActionTypeType.ACTION)
    .map(toEABType),
);
const { events } = useEventTranslationLayer(
  computed(() => undefined),
  changedEvents,
  computed(() => IssueOrder.DESC),
);
const loading = computed(() => loadingEvents.value || loadingTypes.value);

provide(BoardIsLoadingInjectionKey, loading);
provide(ReloadBoardInjectionKey, () => {
  void refetch();
});
</script>

<style scoped lang="scss">
.changed-actions {
  display: flex;
  min-height: 30rem;
  flex-direction: column;
}
</style>
